import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  TextField,
  Container,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Alert,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useQuery } from "./helpers";

const IndexPage = () => {
  const queryParams = useQuery();
  const showThankYouText = queryParams.get("showThankYouText");
  const [query, setQuery] = useState("");
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [summarize, setSummarize] = useState(false);
  const [featuredSources, setFeaturedSources] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [allSources, setAllSources] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeaturedVideos = async () => {
      try {
        setLoadingFeatured(true);
        const response = await axios.get(
          `${process.env.REACT_APP_PRIMARY_SOURCES_API_URI}/api/featured_videos`
        );
        if (response.data && response.data.primary_sources) {
          setFeaturedSources(response.data.primary_sources);
        }
      } catch (err) {
        console.error("Error fetching featured videos:", err);
      } finally {
        setLoadingFeatured(false);
      }
    };

    fetchFeaturedVideos();
  }, []);

  useEffect(() => {
    const fetchAllInterviews = async () => {
      try {
        setLoadingAll(true);
        const response = await axios.get(
          `${process.env.REACT_APP_PRIMARY_SOURCES_API_URI}/api/primary_sources`
        );
        if (response.data && response.data.primary_sources) {
          // Filter out already featured sources
          const filteredSources = response.data.primary_sources.filter(
            (source) =>
              !featuredSources.some((featured) => featured.id === source.id)
          );
          setAllSources(filteredSources);
        }
      } catch (err) {
        console.error("Error fetching all interviews:", err);
      } finally {
        setLoadingAll(false);
      }
    };

    fetchAllInterviews();
  }, [featuredSources]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAnswer("");
    setError("");
    setLoading(true);

    if (!query) {
      setError("Please enter a question.");
      setLoading(false);
      return;
    }

    try {
      const encodedQuestion = encodeURIComponent(query);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/ask`,
        {
          params: {
            question: encodedQuestion,
            summarize: summarize ? "false" : "true",
          },
        }
      );

      if (response.data.answer) {
        setAnswer(response.data.answer);
      } else {
        setError("No answer received from the API.");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred while fetching the answer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Container maxWidth="md" style={{ marginTop: "50px" }}>
        {showThankYouText && (
          <Alert
            sx={{ fontSize: "17px" }}
            icon={<CheckIcon fontSize="17px" />}
            severity="success"
          >
            Thank you! Our team will review your video shortly. You will receive
            an email when it has been added to our database.
          </Alert>
        )}
        <Typography
          variant="h2"
          gutterBottom
          style={{
            fontFamily: "Times New Roman, serif",
            color: "navy",
            fontWeight: 100,
            fontSize: "72px",
            textAlign: "center",
          }}
        >
          Souvenons
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            fontSize: "18px",
            lineHeight: 1.6,
            maxWidth: "680px",
            mx: "auto",
            mt: -1,
            mb: 4,
            textAlign: "center",
          }}
        >
          Souvenons is a place to upload and preserve video and audio
          interviews with anyone. It is built especially for capturing memories
          from past generations, while welcoming stories, reflections, and
          conversations from people of all ages.
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ mt: 2, display: "flex", alignItems: "center" }}
        >
          <TextField
            label="Enter your question, i.e. 'what makes a good life?' or 'what year was <name> born?'"
            variant="outlined"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ ml: 2 }}
          >
            Search
          </Button>
        </Box>
        <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
          <FormControlLabel
            control={
              <Switch
                checked={summarize}
                onChange={(e) => setSummarize(e.target.checked)}
                name="summarize"
              />
            }
            label="Only show exact quotes from sources"
          />
        </Box>
        {loading && <CircularProgress sx={{ mt: 4 }} />}
        {answer && !loading && (
          <Typography variant="h6" sx={{ mt: 4 }}>
            Answer: {answer}
          </Typography>
        )}
        {error && !loading && (
          <Typography variant="body1" color="error" sx={{ mt: 4 }}>
            {error}
          </Typography>
        )}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" gutterBottom>
            Featured Interviews
          </Typography>
          {loadingFeatured ? (
            <CircularProgress sx={{ mt: 4 }} />
          ) : Array.isArray(featuredSources) && featuredSources.length > 0 ? (
            <Grid container spacing={2}>
              {featuredSources.map((source) => (
                <Grid item xs={12} sm={6} md={4} key={source.id}>
                  <Card
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(`/primary_source/${source.id}`)}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      {source.thumbnail_url ? (
                        <CardMedia
                          component="img"
                          height="140"
                          image={source.thumbnail_url}
                          alt={source.title}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: "140px",
                            backgroundColor: "black",
                          }}
                        />
                      )}
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography gutterBottom variant="h6" component="div">
                          {source.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {source.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1" color="text.secondary">
              No featured interviews available.
            </Typography>
          )}
          <br /> <br />
          {/* All Interviews Section */}
          {loadingAll ? (
            <CircularProgress sx={{ mt: 4 }} />
          ) : Array.isArray(allSources) && allSources.length > 0 ? (
            <>
              <Typography variant="h4" gutterBottom sx={{ mt: 6 }}>
                All Interviews
              </Typography>
              <Grid container spacing={2}>
                {allSources.map((source) => (
                  <Grid item xs={12} sm={6} md={4} key={source.id}>
                    <Card
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      <CardActionArea
                        onClick={() => navigate(`/primary_source/${source.id}`)}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                        }}
                      >
                        {source.thumbnail_url ? (
                          <CardMedia
                            component="img"
                            height="140"
                            image={source.thumbnail_url}
                            alt={source.title}
                          />
                        ) : (
                          <Box
                            sx={{ height: "140px", backgroundColor: "black" }}
                          />
                        )}
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography gutterBottom variant="h6" component="div">
                            {source.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {source.description}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <></>
          )}
        </Box>
      </Container>
    </div>
  );
};

export default IndexPage;
